import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, LifeBuoy } from 'lucide-react';

const Contact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Feature flag for phone support - set to true when phone support is available
  const showPhoneSupport = false;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle pre-filling subject from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const subject = searchParams.get('subject');
    if (subject) {
      setFormData(prev => ({
        ...prev,
        subject: decodeURIComponent(subject)
      }));
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'contact',
          ...formData
        }).toString()
      });

      if (response.ok) {
        setIsSubmitted(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: ''
          });
        }, 3000);
      } else {
        console.error('Form submission failed');
        alert('There was an error submitting your form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your form. Please try again.');
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'contact@gscsnh.com',
      subDetails: 'We typically reply within 24 hours',
      clickable: true,
      onClick: () => {
        window.location.href = 'mailto:contact@gscsnh.com';
      }
    },
    ...(showPhoneSupport ? [{
      icon: Phone,
      title: 'Call Us',
      details: '+1 (603) 123-4567',
      subDetails: 'Mon-Fri 9AM-6PM EST',
      clickable: false
    }] : []),
    {
      icon: LifeBuoy,
      title: 'Support',
      details: 'Check our support page for Information',
      subDetails: 'Support Contract Required',
      clickable: true,
      onClick: () => {
        navigate('/expert-support');
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Mon-Fri: 9AM-6PM',
      subDetails: 'Off-Hours Support Available',
      clickable: false
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stop Guessing. Start Knowing.
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed mb-8">
            In 15 minutes, we'll map your current data silos and show you exactly where an Intelligence Layer will reclaim 10+ hours of your week.
          </p>
          <div className="inline-block bg-primary-600/20 border border-primary-400 rounded-xl px-6 py-3">
            <p className="text-primary-200 font-medium">
              15-Minute Workflow Audit · No Commitment · See How Your Data Becomes Decisions
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold mb-8">
              How It Works
            </h3>

            <div className="space-y-6 mb-10">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary-600 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center text-lg">
                  1
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">
                    Schedule Your Audit
                  </h4>
                  <p className="text-gray-300">
                    Fill out the form and we'll schedule a quick 15-minute video call at your convenience.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary-600 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center text-lg">
                  2
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">
                    We Map Your Silos
                  </h4>
                  <p className="text-gray-300">
                    We'll identify your disconnected data sources and the questions you're manually answering every day.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary-600 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center text-lg">
                  3
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">
                    See Your Time Savings
                  </h4>
                  <p className="text-gray-300">
                    We'll show you exactly how an Intelligence Layer would work for your business and estimate your time reclaimed.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-bold">
                Contact Information
              </h3>
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                const Component = info.clickable ? 'button' : 'div';
                return (
                  <Component
                    key={index}
                    className={`flex items-start space-x-4 w-full text-left ${info.clickable ? 'hover:bg-white/10 -m-3 p-3 rounded-lg transition-colors cursor-pointer group' : ''}`}
                    onClick={info.onClick}
                  >
                    <div className={`flex-shrink-0 bg-primary-600/30 p-3 rounded-xl ${info.clickable ? 'group-hover:bg-primary-600/50 transition-colors' : ''}`}>
                      <IconComponent className="h-6 w-6 text-primary-200" />
                    </div>
                    <div>
                      <h4 className={`text-lg font-semibold mb-1 ${info.clickable ? 'group-hover:text-primary-300 transition-colors' : ''}`}>
                        {info.title}
                      </h4>
                      <p className="text-gray-200 font-medium">
                        {info.details}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {info.subDetails}
                      </p>
                    </div>
                  </Component>
                );
              })}
            </div>

          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-primary-400">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="bg-green-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Audit Request Received!
                </h3>
                <p className="text-gray-600">
                  We'll be in touch within 24 hours to schedule your 15-minute Workflow Audit.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Request Your Workflow Audit
                </h3>
                <p className="text-gray-600 mb-8">
                  Tell us about your business and we'll show you how to turn data silos into instant insights.
                </p>
                
                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                >
                  {/* Hidden fields for Netlify Forms */}
                  <input type="hidden" name="form-name" value="contact" />
                  <div style={{ display: 'none' }}>
                    <input name="bot-field" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-gray-900"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-gray-900"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-gray-900"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        What brings you here? *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-gray-900"
                      >
                        <option value="">Select an option...</option>
                        <option value="Workflow Audit Request">15-Minute Workflow Audit (Recommended)</option>
                        <option value="Finance Intelligence">Finance Intelligence</option>
                        <option value="Operational Intelligence">Operational Intelligence</option>
                        <option value="Bespoke Orchestration">Bespoke Orchestration</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Tell us about your data sources and business questions *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none text-gray-900"
                      placeholder="Example: We use QuickBooks for accounting, Square for POS, and Excel for tracking inventory. I spend hours each week manually pulling reports to answer 'What's my actual margin by product line?'"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center group shadow-xl hover:shadow-2xl"
                  >
                    Request My Workflow Audit
                    <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <p className="text-center text-gray-500 text-sm mt-4">
                    We'll schedule your 15-minute audit within 24 hours. No sales pitch—just a clear roadmap.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;